import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import ScrollTools from "@/components/feature/ScrollTools";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import StatusBadge from "@/components/base/StatusBadge";
import { useSeo } from "@/hooks/useSeo";
import { cases } from "@/mocks/cases";
import CaseTimeline from "@/pages/case-detail/components/CaseTimeline";
import ResponseCompare from "@/pages/case-detail/components/ResponseCompare";
import WindChart from "@/pages/case-detail/components/WindChart";
import RelatedCases from "@/pages/case-detail/components/RelatedCases";

const sections = [
  { id: "focus", label: "争议焦点" },
  { id: "timeline", label: "事件时间线" },
  { id: "responses", label: "双方回应对照" },
  { id: "wind", label: "舆论风向变化" },
  { id: "clarify", label: "澄清汇总" },
  { id: "verdict", label: "观察结论" },
];

export default function CaseDetail() {
  const { slug } = useParams<{ slug: string }>();
  const item = cases.find((entry) => entry.slug === slug);

  const related = item
    ? [...cases.filter((entry) => entry.category === item.category && entry.id !== item.id),
       ...cases.filter((entry) => entry.category !== item.category && entry.id !== item.id)].slice(0, 3)
    : [];

  useSeo({
    title: item ? `${item.title}｜时间线与双方回应梳理 - 瓜田黑料` : "案例未找到 - 瓜田黑料",
    description: item
      ? `${item.summary} 本文梳理该事件的完整时间线、双方回应对照、舆论风向变化与澄清汇总，争议焦点：${item.focus}。`
      : "该案例不存在或已被移除，可返回案例库浏览其他档案。",
    keywords: item ? `瓜田黑料,${item.tags.join(",")},反转剧情,双方回应,舆论风向` : "瓜田黑料,案例库",
    path: `/cases/${slug ?? ""}`,
    image: item?.cover,
    type: "article",
    jsonLd: item
      ? [
          {
            "@context": "https://schema.org",
            "@type": "Article",
            headline: item.title,
            description: item.summary,
            image: [item.cover],
            datePublished: item.publishDate,
            dateModified: item.updatedDate,
            inLanguage: "zh-CN",
            articleSection: item.category,
            keywords: item.tags.join(","),
            about: item.focus,
            author: { "@type": "Organization", name: "瓜田黑料" },
            publisher: {
              "@type": "Organization",
              name: "瓜田黑料",
              url: "https://stranding.cn/",
            },
            mainEntityOfPage: {
              "@type": "WebPage",
              "@id": `https://stranding.cn/cases/${item.slug}`,
            },
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: "https://stranding.cn/" },
              { "@type": "ListItem", position: 2, name: "案例库", item: "https://stranding.cn/cases" },
              {
                "@type": "ListItem",
                position: 3,
                name: item.title,
                item: `https://stranding.cn/cases/${item.slug}`,
              },
            ],
          },
        ]
      : undefined,
  });

  if (!item) {
    return (
      <div className="flex min-h-screen w-full flex-col bg-background-50">
        <SiteHeader />
        <main className="mx-auto flex w-full max-w-[1440px] flex-1 flex-col items-center justify-center px-4 py-24 text-center md:px-6">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-background-500">
            <i className="ri-file-unknow-line text-2xl" aria-hidden="true" />
          </span>
          <h1 className="mt-6 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">没有找到这份档案</h1>
          <p className="mt-3 max-w-lg text-sm leading-relaxed text-foreground-600">
            它可能已经被并入其他案例，或者链接输入有误。回到案例库，你能看到当前全部已归档的事件。
          </p>
          <Link
            to="/cases"
            className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-800 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700"
          >
            <i className="ri-archive-2-line text-base" aria-hidden="true" />
            返回案例库
          </Link>
        </main>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <SiteHeader />
      <main className="w-full">
        <section className="w-full border-b border-background-200 bg-background-100">
          <div className="mx-auto max-w-[1440px] px-4 py-8 md:px-6 md:py-12">
            <Breadcrumbs
              items={[{ label: "首页", to: "/" }, { label: "案例库", to: "/cases" }, { label: item.title }]}
            />
            <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-10">
              <div className="lg:col-span-7">
                <div className="flex flex-wrap items-center gap-2">
                  <Link
                    to={`/cases?category=${encodeURIComponent(item.category)}`}
                    className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-secondary-100 px-2.5 py-1 text-[11px] font-medium text-secondary-900 transition-colors hover:bg-secondary-200"
                  >
                    <i className="ri-price-tag-3-line text-xs" aria-hidden="true" />
                    {item.category}
                  </Link>
                  <StatusBadge label={item.status} />
                </div>
                <h1 className="mt-4 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-4xl">
                  {item.title}
                </h1>
                <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">{item.summary}</p>

                <dl className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-3 border-t border-background-300 pt-5 text-[11px] text-foreground-500">
                  <div className="flex items-center gap-1.5">
                    <dt className="sr-only">首次归档</dt>
                    <dd className="flex items-center gap-1.5">
                      <i className="ri-calendar-line text-sm" aria-hidden="true" />
                      首次归档 {item.publishDate}
                    </dd>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <dt className="sr-only">最近更新</dt>
                    <dd className="flex items-center gap-1.5">
                      <i className="ri-refresh-line text-sm" aria-hidden="true" />
                      最近更新 {item.updatedDate}
                    </dd>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <dt className="sr-only">阅读时长</dt>
                    <dd className="flex items-center gap-1.5">
                      <i className="ri-time-line text-sm" aria-hidden="true" />
                      {item.readTime}
                    </dd>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <dt className="sr-only">讨论热度</dt>
                    <dd className="flex items-center gap-1.5">
                      <i className="ri-fire-line text-sm text-accent-500" aria-hidden="true" />
                      热度 {item.heat}
                    </dd>
                  </div>
                </dl>

                <ul className="mt-5 flex flex-wrap gap-2">
                  {item.tags.map((tag) => (
                    <li
                      key={tag}
                      className="rounded-md border border-background-300 px-2.5 py-1 text-[11px] text-foreground-600"
                    >
                      #{tag}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="lg:col-span-5">
                <figure className="overflow-hidden rounded-lg border border-background-300 bg-background-50">
                  <img
                    src={item.cover}
                    alt={`${item.title}的案例主视觉`}
                    title={`${item.title} - 瓜田黑料案例档案`}
                    className="h-56 w-full object-top md:h-72"
                  />
                  <figcaption className="border-t border-background-200 px-4 py-3 text-[11px] text-foreground-500">
                    案例主视觉 · 内容已作匿名化处理，仅用于呈现信息流转过程
                  </figcaption>
                </figure>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full py-10 md:py-14">
          <div className="mx-auto grid max-w-[1440px] grid-cols-1 gap-10 px-4 md:px-6 lg:grid-cols-12 lg:gap-12">
            <div className="flex flex-col gap-12 lg:col-span-8">
              <div id="focus" className="scroll-mt-32">
                <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">争议焦点</h2>
                <p className="mt-4 rounded-lg border-l-4 border-accent-500 bg-accent-50/70 px-5 py-4 font-heading text-sm leading-relaxed text-foreground-800 md:text-base">
                  {item.focus}
                </p>
                <p className="mt-4 text-sm leading-relaxed text-foreground-600">
                  先把这个焦点单独拎出来，是因为大部分舆论偏移都发生在这一点上。围绕它之外的次要争议，
                  我们放在时间线与回应汇总里作为背景信息，不单独展开。
                </p>
              </div>

              <div id="timeline" className="scroll-mt-32">
                <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">事件时间线</h2>
                <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                  按发布时间正序排列。顺序本身是这份档案里最重要的信息之一。
                </p>
                <div className="mt-6">
                  <CaseTimeline nodes={item.timeline} />
                </div>
              </div>

              <div id="responses" className="scroll-mt-32">
                <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">双方回应对照</h2>
                <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                  两栏并列呈现，篇幅与位置保持对等。看回应时，先找它正面回答了哪个问题，再找它绕开了哪个问题。
                </p>
                <div className="mt-6">
                  <ResponseCompare items={item.responses} />
                </div>
              </div>

              <div id="wind" className="scroll-mt-32">
                <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">舆论风向变化</h2>
                <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                  每一根横条代表一个阶段里三类声音的占比。数值来自公开讨论的抽样统计，只用于呈现方向，不代表精确民意。
                </p>
                <div className="mt-6 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
                  <WindChart stages={item.wind} />
                </div>
              </div>

              <div id="clarify" className="scroll-mt-32">
                <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">澄清汇总</h2>
                <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                  逐条对应最初的指控，标注哪些成立、哪些不成立、哪些仍在核实。
                </p>
                <ul className="mt-6 flex flex-col gap-3">
                  {item.clarifications.map((line, index) => (
                    <li
                      key={line}
                      className="flex gap-3 rounded-lg border border-background-200 bg-background-50 px-4 py-3.5"
                    >
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-primary-100 font-heading text-[11px] font-bold text-primary-800">
                        {String(index + 1).padStart(2, "0")}
                      </span>
                      <span className="text-sm leading-relaxed text-foreground-700">{line}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div id="verdict" className="scroll-mt-32">
                <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">观察结论</h2>
                <blockquote className="mt-4 rounded-lg bg-primary-950 px-5 py-5 text-sm leading-relaxed text-background-100 md:px-7 md:py-6 md:text-base">
                  {item.verdict}
                </blockquote>
                <p className="mt-4 text-xs leading-relaxed text-foreground-500">
                  以上结论基于归档时公开可查的信息。如果出现新的可核验材料，我们会更新这份档案并标注修改时间。
                </p>
              </div>

              <RelatedCases items={related} />
            </div>

            <aside className="lg:col-span-4">
              <div className="flex flex-col gap-6 lg:sticky lg:top-32">
                <nav
                  aria-label="本文目录"
                  className="rounded-lg border border-background-200 bg-background-50 p-5"
                >
                  <h2 className="font-heading text-sm font-semibold text-foreground-950">本文目录</h2>
                  <ul className="mt-4 flex flex-col gap-1">
                    {sections.map((section, index) => (
                      <li key={section.id}>
                        <a
                          href={`#${section.id}`}
                          className="flex items-center gap-3 rounded-md px-3 py-2 text-xs text-foreground-600 transition-colors hover:bg-background-100 hover:text-foreground-950"
                        >
                          <span className="font-heading text-[10px] text-background-500">
                            {String(index + 1).padStart(2, "0")}
                          </span>
                          {section.label}
                        </a>
                      </li>
                    ))}
                  </ul>
                </nav>

                <div className="rounded-lg border border-background-200 bg-background-100 p-5">
                  <h2 className="flex items-center gap-2 font-heading text-sm font-semibold text-foreground-950">
                    <i className="ri-shield-check-line text-base text-primary-700" aria-hidden="true" />
                    阅读提示
                  </h2>
                  <ul className="mt-4 flex flex-col gap-3 text-xs leading-relaxed text-foreground-600">
                    <li className="flex gap-2">
                      <i className="ri-checkbox-blank-circle-line mt-0.5 text-[10px] text-accent-500" aria-hidden="true" />
                      案例已匿名化处理，不对任何主体作出评价或认定。
                    </li>
                    <li className="flex gap-2">
                      <i className="ri-checkbox-blank-circle-line mt-0.5 text-[10px] text-accent-500" aria-hidden="true" />
                      风向数据为抽样统计，用于呈现方向而非精确民意。
                    </li>
                    <li className="flex gap-2">
                      <i className="ri-checkbox-blank-circle-line mt-0.5 text-[10px] text-accent-500" aria-hidden="true" />
                      发现内容有误，可通过邮箱申请补充说明。
                    </li>
                  </ul>
                </div>

                <div className="rounded-lg border border-accent-200 bg-accent-50 p-5">
                  <h2 className="font-heading text-sm font-semibold text-foreground-950">手上材料更完整？</h2>
                  <p className="mt-2 text-xs leading-relaxed text-foreground-700">
                    如果你掌握原始文件、完整视频或可核验的记录，欢迎通过线索表单投递给编辑。
                  </p>
                  <Link
                    to="/guide"
                    className="mt-4 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-800 px-4 py-2 text-xs font-medium text-background-50 transition-colors hover:bg-primary-700"
                  >
                    <i className="ri-checkbox-circle-line text-sm" aria-hidden="true" />
                    先看核验标准
                  </Link>
                </div>
              </div>
            </aside>
          </div>
        </section>
      </main>
      <SiteFooter />
      <ScrollTools />
    </div>
  );
}